import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { useIsMobile } from "../useIsMobile";
import { Close } from "@mui/icons-material";
import { useUILocale } from "../UILocale";

// headline OR aria-label are required for accessibility but both should not be used together, headline is preferred when possible
export type WithHeadline = { headline: string; "aria-label"?: never };
export type WithoutHeadline = { headline?: never; "aria-label": string };
type FormDialogProps = Omit<
  DialogProps,
  "onClose" | "aria-label" | "aria-labelledby" | "role" | "aria-describedby"
> &
  (WithHeadline | WithoutHeadline) & {
    formId: string;
    confirmButtonText?: React.ReactNode;
    dismissButtonText?: string;
    /**
     * Prevent wrapping children in DialogContent, for cases where the form needs to be full bleed
     * defaults to false
     */
    disableDialogContent?: boolean;
    /**
     * Auto focus the dismiss button when the dialog opens
     * Defaults to false, Forms should generally autofocus their first input
     */
    autoFocusCancelButton?: boolean;
    children: React.ReactNode;
    onClose?: () => void;
    /**
     * Disables the confirm button. Used for instances where we need to block submission but there is not a possible form validation error
     * Be very careful using this and ensure the error state is read aloud to screen readers, as this can be an accessibility issue if not handled properly
     * Defaults to false
     */
    submitButtonDisabled?: boolean;
    /**
     * Indicates that the form is in a submitting state, disables all inputs, buttons, escape key and backdrop closing, and shows a loading indicator on the confirm button.
     * Optional because some form dialogs (like admin) don't have async form submissions on confirm
     * Defaults to false
     */
    isSubmitting?: boolean;
  };

export function FormDialog({
  formId,
  onClose,
  confirmButtonText: confirmButtonTextOverride,
  dismissButtonText: dismissButtonTextOverride,
  disableDialogContent = false,
  autoFocusCancelButton = false,
  submitButtonDisabled = false,
  isSubmitting = false,
  headline,
  "aria-label": ariaLabel,
  ...props
}: FormDialogProps) {
  // Adapts from a Full Screen Dialog on Mobile to a Basic Dialog on Desktop
  const isMobile = useIsMobile();
  const { dialog } = useUILocale();
  const confirmButtonText = confirmButtonTextOverride || dialog.save;
  const dismissButtonText = dismissButtonTextOverride || dialog.cancel;

  const handleOnClose = useCallback(
    (_: any, reason: "backdropClick" | "escapeKeyDown") => {
      if (isSubmitting) {
        return; // prevent closing while submitting
      }
      // Prevent closing on backdrop click to avoid accidental closes on form dialogs
      if (reason === "backdropClick") {
        return;
      }
      if (onClose) {
        onClose();
      }
    },
    [onClose, isSubmitting],
  );

  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && autoFocusCancelButton && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [open, autoFocusCancelButton]);

  const dialogContentPaddingTop = isMobile ? 0 : undefined; // remove padding top in mobile mode to account for AppBar, DialogTitle + DialogContent handle this already

  const hasHeadline = Boolean(headline); // handles null, undefined, and empty string
  const hasAriaLabel = Boolean(ariaLabel); // handles null, undefined, and empty string

  if (!hasHeadline && !hasAriaLabel) {
    throw new Error(
      "FormDialog requires either a headline or an aria-label for accessibility",
    );
  }

  const headlineId = hasHeadline ? `${formId}-headline` : undefined;
  const a11yProps = hasHeadline
    ? { "aria-labelledby": headlineId }
    : { "aria-label": ariaLabel };

  return (
    <Dialog
      maxWidth="xs" // can be overriden via props
      {...props}
      slotProps={{
        // @ts-ignore - MUI's Dialog doesn't forward aria-label or aria-labelledby to the paper element which causes accessibility issues for screen readers, so we need to add it here.
        paper: {
          ...a11yProps,
        },
      }}
      onClose={handleOnClose}
      fullScreen={isMobile}
      fullWidth={true} // used in conjuntion with maxWidth when not fullScreen
    >
      {isMobile && (
        <AppBar position="relative" elevation={0} color="default">
          <Toolbar>
            <IconButton
              onClick={onClose}
              ref={cancelButtonRef}
              aria-label={dismissButtonText}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
            <Typography
              sx={{
                flexGrow: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {hasHeadline && <span id={headlineId}>{headline}</span>}
            </Typography>
            <Button
              color="primary"
              type="submit"
              form={formId}
              disabled={submitButtonDisabled}
              loading={isSubmitting}
              sx={{ whiteSpace: "nowrap" }}
            >
              {confirmButtonText}
            </Button>
          </Toolbar>
        </AppBar>
      )}
      {!isMobile && hasHeadline && (
        <DialogTitle id={headlineId}>{headline}</DialogTitle>
      )}
      {disableDialogContent ? (
        props.children
      ) : (
        <DialogContent sx={{ paddingTop: dialogContentPaddingTop }}>
          {props.children}
        </DialogContent>
      )}
      {!isMobile && (
        <DialogActions>
          <Button
            ref={cancelButtonRef}
            disabled={isSubmitting}
            onClick={onClose}
          >
            {dismissButtonText}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form={formId}
            disabled={submitButtonDisabled}
            loading={isSubmitting}
          >
            {confirmButtonText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
